'use client';
import Image from "next/image";
import {useTranslations} from 'next-intl';
import {formatProductReviewDate} from '@/libs/date.util';
import defaultPhoto from '@/assets/img/user/default.png';
import {useState} from 'react';
import {usePaginatedQuery} from '@/libs/api/baseRequests';
import {GET_PRODUCT_REVIEW_REPLIES_PAGINATED_QUERY} from '@/graphql/queries';
import {ClientTypes, ProductReviewRepliesPaginatedTake} from '@/libs/constants/common.constants';
import ProductReviewRepliesEditDelete from '@/components/shared/products/ProductReviewRepliesEditDelete';


export default function ProductReviewReplies({ repliesCount, reviewId, reviewIdx, children: newReplies, removeLocalReplies, openDeleteModal }) {
  const t = useTranslations('product');
  const d = useTranslations('date.months');
  
  const [repliesOpened, setRepliesOpened] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  
  const { items: replies, setItems: setReplies, fetchMoreData: fetchMoreReplies, hasMore, resetPagination } = usePaginatedQuery(
      GET_PRODUCT_REVIEW_REPLIES_PAGINATED_QUERY, {
        reviewId,
        take: ProductReviewRepliesPaginatedTake,
        skip: 0,
      },
      { skipFirst: true, hasMore: true },
  );
  
  const onGetRepliesClick = async () => {
    setReplyLoading(true);
    if (!repliesOpened) {
      await fetchMoreReplies();
      removeLocalReplies(reviewIdx);
    } else {
      resetPagination();
    }
    setReplyLoading(false);
    setRepliesOpened(prevState => !prevState);
  }
  
  const onGetMoreRepliesClick = async () => {
    setReplyLoading(true);
    await fetchMoreReplies();
    setReplyLoading(false);
  }
  
  return (
      <>
        {
          repliesCount > 0 ? (
              <button
                  className="get-reply-button"
                  onClick={() => onGetRepliesClick()}
              >
                <i className={`fas fa-chevron-${repliesOpened ? 'up' : 'down'}`}></i>{' '}
                { t('nReplies', {count: repliesCount}) }
              </button>
          ) : ''
        }
        <div className="ltn__comment-inner review-reply-item">
          <ul>
            {
              replies.length ? (
                  replies.map((reply, idx) => (
                      <li key={idx} className="reply-item">
                        <div className="ltn__comment-item clearfix">
                          <div className="ltn__commenter-img">
                            <Image
                                key={`${reply.client.fullName}-${idx}`}
                                src={reply.client.image ?? defaultPhoto}
                                alt={`${reply.client.fullName}-${idx + 1}`}
                                placeholder="blur"
                                blurDataURL={reply.client.fullName}
                                width={100}
                                height={100}
                                style={{height: 'auto', aspectRatio: 1, objectFit: 'cover'}}
                            />
                          </div>
                          <div className="ltn__commenter-comment">
                            <h6>
                              {reply.client.fullName}
                              {
                                reply.client.clientType === ClientTypes.ADMIN ? (
                                    <span className="client-review">
                                      ( {t('admin')} )
                                    </span>
                                ) : ''
                              }
                            </h6>
                            <div>
                              <div className="review-date">
                                {formatProductReviewDate(reply.createdAt, d)}
                              </div>
                            </div>
                          </div>
                          <div className="reply-comment">
                            <ProductReviewRepliesEditDelete
                                currentReply={reply}
                                setReplies={setReplies}
                                replyIndex={idx}
                                openDeleteModal={openDeleteModal}
                            />
                          </div>
                        </div>
                      </li>
                  ))
              ) : ''
            }
            {
              hasMore && repliesOpened ? (
                  <button
                      className="get-reply-button"
                      onClick={() => onGetMoreRepliesClick()}
                  >
                    <i className={`rotate fas fa-arrow-turn-up`}></i>{' '}
                    { t('showMoreReplies') }
                  </button>
              ) : ''
            }
            {
              replyLoading ? (
                  <li className="reply-loading">
                    {t('loading')}
                  </li>
              ) : ''
            }
            { newReplies }
          </ul>
        </div>
      </>
  );
}
